import React, { useCallback, useEffect } from 'react'

import { useAuthInfo } from '@sweetauth/core'

import BillingOrgWrapper from '@/components/billing/BillingOrgWrapper'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'

import { setDatadogUser } from '@/lib/datadog'

import type { BillingOrganization } from '@/types/BillingOrganization'
import type { NextPageWithLayout } from '@/types/next'

// TODO: this forces all authenticated components to be client side rendered while also allowing proper login rerouting,
// (see the component definition for more information) which means if we ever want to move authenticated content to SSR,
// we will need a major refactor;
// NOTE: removing this temporarily to see if we don't need it anymore (09/10/2024)
// const DynamicBillingOrgWrapper = dynamic(
//   () => import('@/components/billing/BillingOrgWrapper'),
//   { ssr: false }
// )

export const requireAuth = (
  Component: NextPageWithLayout | React.ComponentType<any> | React.FC<any>,
) => {
  Component.requiresAuth = true
  return Component
}

export const withAuth = (
  Component: React.ComponentType | NextPageWithLayout,
  props?: React.ComponentProps<typeof Component>,
) => {
  const getLayout = Component.getLayout || ((page: React.ReactNode) => page)

  const getMetadata =
    Component.getMetadata && typeof Component.getMetadata === 'function'
      ? Component.getMetadata
      : () => null

  // Create the metadata element only once
  const metadataElement = getMetadata()

  if (Component.requiresAuth === false) {
    // returning outside of the HOC to prevent double rendering for SSR pages
    return (
      <>
        {/*
          wrap in a fragment, otherwise React throws the following warning:
          Functions are not valid as a React child.
          This may happen if you return a Component instead of <Component /> from render. Or maybe you meant to call this function rather than return it.
        */}
        <>{metadataElement}</>
        {getLayout(<Component {...props} />)}
      </>
    )
  }

  return (
    <>
      {metadataElement}
      <BillingOrgWrapper>
        <>
          <DatadogAuthWatcher />
          {getLayout(<Component {...props} />)}
        </>
      </BillingOrgWrapper>
    </>
  )
}

export const DatadogAuthWatcher = () => {
  const { user } = useAuthInfo()
  const selectBillingOrgSnapshot = useCallback(
    (org: BillingOrganization | null) =>
      org
        ? {
            name: org.name,
            tier: org.tier,
          }
        : null,
    [],
  )
  const { data: billingOrgSnapshot } = useBillingOrganization({
    select: selectBillingOrgSnapshot,
  })

  useEffect(() => {
    if (user) {
      setDatadogUser({
        id: user.userId,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        org: billingOrgSnapshot?.name ?? null,
        tier: billingOrgSnapshot?.tier ?? null,
      })
    } else {
      setDatadogUser(null)
    }
  }, [user, billingOrgSnapshot])

  return null
}
